import { useEffect, useState } from "react";
import { userManagement } from "../../../api/userManagement.service.js";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type LookupState =
  | { status: "pending" }
  | { status: "resolved"; label: string }
  | { status: "failed" };

const cache = new Map<string, LookupState>();
const inflight = new Map<string, Promise<void>>();
const subscribers = new Set<() => void>();

function notify(): void {
  for (const subscriber of subscribers) {
    subscriber();
  }
}

async function lookup(id: string): Promise<void> {
  cache.set(id, { status: "pending" });
  try {
    const user = await userManagement.getUserById(id);
    cache.set(id, { status: "resolved", label: user.email || user.name || id });
  } catch {
    cache.set(id, { status: "failed" });
  } finally {
    notify();
  }
}

function ensureLookup(id: string): void {
  if (cache.has(id)) return;
  let pending = inflight.get(id);
  if (!pending) {
    pending = lookup(id).finally(() => inflight.delete(id));
    inflight.set(id, pending);
  }
}

function shortUuid(uuid: string): string {
  return uuid.slice(0, 8);
}

/**
 * Render a user identifier as their email (or name, falling back to the raw
 * UUID). Looks up the users-api once per UUID and shares the result across all
 * mounted UserLabel instances. Non-UUID values (e.g. "system", "cli") render
 * verbatim. A title attribute exposes the raw UUID for debugging.
 */
export function UserLabel({ value }: { value: string | null | undefined }) {
  const trimmed = (value ?? "").trim();
  const isUuid = trimmed.length > 0 && UUID_PATTERN.test(trimmed);

  const [, setTick] = useState(0);

  useEffect(() => {
    if (!isUuid) return;
    ensureLookup(trimmed);
    const subscriber = () => setTick(t => t + 1);
    subscribers.add(subscriber);
    return () => {
      subscribers.delete(subscriber);
    };
  }, [trimmed, isUuid]);

  if (!trimmed) {
    return <span>-</span>;
  }
  if (!isUuid) {
    return <span>{trimmed}</span>;
  }

  const state = cache.get(trimmed);
  if (!state || state.status === "pending") {
    return <span title={trimmed}>{shortUuid(trimmed)}…</span>;
  }
  if (state.status === "failed") {
    return <span title={trimmed}>{shortUuid(trimmed)}</span>;
  }
  return <span title={trimmed}>{state.label}</span>;
}
